import { useAuth } from "../contexts/AuthContext";

// Permission constants for easy reference
export const PERMISSIONS = {
  // Job Management
  CAN_CREATE_JOB: "CAN_CREATE_JOB",
  CAN_EDIT_JOB: "CAN_EDIT_JOB",
  CAN_DELETE_JOB: "CAN_DELETE_JOB",
  CAN_CLOSE_JOB: "CAN_CLOSE_JOB",
  CAN_TRACK_JOB: "CAN_TRACK_JOB",

  // User Management
  CAN_CREATE_USER: "CAN_CREATE_USER",
  CAN_EDIT_USER: "CAN_EDIT_USER",
  CAN_DELETE_USER: "CAN_DELETE_USER",

  // Driver Management
  CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER:
    "CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER",
  CAN_VIEW_DRIVERS: "CAN_VIEW_DRIVERS",
  CAN_CREATE_DRIVER: "CAN_CREATE_DRIVER",
  CAN_EDIT_DRIVER: "CAN_EDIT_DRIVER",
  CAN_DELETE_DRIVER: "CAN_DELETE_DRIVER",

  // Transporter Management
  CAN_CRUD_TRANSPORTER: "CAN_CRUD_TRANSPORTER",
  CAN_VIEW_TRANSPORTERS: "CAN_VIEW_TRANSPORTERS",
  CAN_CREATE_TRANSPORTER: "CAN_CREATE_TRANSPORTER",
  CAN_EDIT_TRANSPORTER: "CAN_EDIT_TRANSPORTER",
  CAN_DELETE_TRANSPORTER: "CAN_DELETE_TRANSPORTER",

  // Federation Management
  CAN_CRUD_FEDERATION: "CAN_CRUD_FEDERATION",
  CAN_VIEW_FEDERATIONS: "CAN_VIEW_FEDERATIONS",
  CAN_CREATE_FEDERATION: "CAN_CREATE_FEDERATION",
  CAN_EDIT_FEDERATION: "CAN_EDIT_FEDERATION",
  CAN_DELETE_FEDERATION: "CAN_DELETE_FEDERATION",

  // Vehicle Management
  CAN_CRUD_TRUCK_TRAILER_FOR_SELF_OR_TRANSPORTER:
    "CAN_CRUD_TRUCK_TRAILER_FOR_SELF_OR_TRANSPORTER",
  CAN_VIEW_VEHICLES: "CAN_VIEW_VEHICLES",
  CAN_CREATE_VEHICLE: "CAN_CREATE_VEHICLE",
  CAN_EDIT_VEHICLE: "CAN_EDIT_VEHICLE",
  CAN_DELETE_VEHICLE: "CAN_DELETE_VEHICLE",

  // Trip Management
  CAN_VIEW_TRIPS: "CAN_VIEW_TRIPS",
  CAN_CREATE_TRIP: "CAN_CREATE_TRIP",
  CAN_EDIT_TRIP: "CAN_EDIT_TRIP",
  CAN_DELETE_TRIP: "CAN_DELETE_TRIP",
  CAN_TRACK_TRIP: "CAN_TRACK_TRIP",

  // Reports
  CAN_VIEW_REPORTS: "CAN_VIEW_REPORTS",
  CAN_EXPORT_REPORTS: "CAN_EXPORT_REPORTS",

  // Settings
  CAN_VIEW_SETTINGS: "CAN_VIEW_SETTINGS",
  CAN_EDIT_SETTINGS: "CAN_EDIT_SETTINGS",
} as const;

// Permission groups for common operations
export const PERMISSION_GROUPS = {
  JOB_MANAGEMENT: [
    PERMISSIONS.CAN_CREATE_JOB,
    PERMISSIONS.CAN_EDIT_JOB,
    PERMISSIONS.CAN_DELETE_JOB,
    PERMISSIONS.CAN_CLOSE_JOB,
    PERMISSIONS.CAN_TRACK_JOB,
  ],
  USER_MANAGEMENT: [
    PERMISSIONS.CAN_CREATE_USER,
    PERMISSIONS.CAN_EDIT_USER,
    PERMISSIONS.CAN_DELETE_USER,
  ],
  DRIVER_MANAGEMENT: [
    PERMISSIONS.CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER,
    PERMISSIONS.CAN_VIEW_DRIVERS,
    PERMISSIONS.CAN_CREATE_DRIVER,
    PERMISSIONS.CAN_EDIT_DRIVER,
    PERMISSIONS.CAN_DELETE_DRIVER,
  ],
  TRANSPORTER_MANAGEMENT: [
    PERMISSIONS.CAN_CRUD_TRANSPORTER,
    PERMISSIONS.CAN_VIEW_TRANSPORTERS,
    PERMISSIONS.CAN_CREATE_TRANSPORTER,
    PERMISSIONS.CAN_EDIT_TRANSPORTER,
    PERMISSIONS.CAN_DELETE_TRANSPORTER,
  ],
  FEDERATION_MANAGEMENT: [
    PERMISSIONS.CAN_CRUD_FEDERATION,
    PERMISSIONS.CAN_VIEW_FEDERATIONS,
    PERMISSIONS.CAN_CREATE_FEDERATION,
    PERMISSIONS.CAN_EDIT_FEDERATION,
    PERMISSIONS.CAN_DELETE_FEDERATION,
  ],
  VEHICLE_MANAGEMENT: [
    PERMISSIONS.CAN_CRUD_TRUCK_TRAILER_FOR_SELF_OR_TRANSPORTER,
    PERMISSIONS.CAN_VIEW_VEHICLES,
    PERMISSIONS.CAN_CREATE_VEHICLE,
    PERMISSIONS.CAN_EDIT_VEHICLE,
    PERMISSIONS.CAN_DELETE_VEHICLE,
  ],
  TRIP_MANAGEMENT: [
    PERMISSIONS.CAN_VIEW_TRIPS,
    PERMISSIONS.CAN_CREATE_TRIP,
    PERMISSIONS.CAN_EDIT_TRIP,
    PERMISSIONS.CAN_DELETE_TRIP,
    PERMISSIONS.CAN_TRACK_TRIP,
  ],
  REPORTS: [PERMISSIONS.CAN_VIEW_REPORTS, PERMISSIONS.CAN_EXPORT_REPORTS],
  SETTINGS: [PERMISSIONS.CAN_VIEW_SETTINGS, PERMISSIONS.CAN_EDIT_SETTINGS],
} as const;

export const usePermissions = () => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, userProfile } =
    useAuth();

  return {
    // Individual permission checks
    can: hasPermission,

    // Multiple permission checks
    canAny: hasAnyPermission,
    canAll: hasAllPermissions,

    // Group permission checks
    canManageJobs: () => hasAnyPermission(PERMISSION_GROUPS.JOB_MANAGEMENT),
    canManageUsers: () => hasAnyPermission(PERMISSION_GROUPS.USER_MANAGEMENT),
    canManageDrivers: () =>
      hasAnyPermission(PERMISSION_GROUPS.DRIVER_MANAGEMENT),
    canManageTransporters: () =>
      hasAnyPermission(PERMISSION_GROUPS.TRANSPORTER_MANAGEMENT),
    canManageVehicles: () =>
      hasAnyPermission(PERMISSION_GROUPS.VEHICLE_MANAGEMENT),
    canManageTrips: () => hasAnyPermission(PERMISSION_GROUPS.TRIP_MANAGEMENT),
    canViewReports: () => hasAnyPermission(PERMISSION_GROUPS.REPORTS),
    canManageSettings: () => hasAnyPermission(PERMISSION_GROUPS.SETTINGS),

    // Specific permission checks
    canCreateJob: () => hasPermission(PERMISSIONS.CAN_CREATE_JOB),
    canEditJob: () => hasPermission(PERMISSIONS.CAN_EDIT_JOB),
    canDeleteJob: () => hasPermission(PERMISSIONS.CAN_DELETE_JOB),
    canCloseJob: () => hasPermission(PERMISSIONS.CAN_CLOSE_JOB),
    canTrackJob: () => hasPermission(PERMISSIONS.CAN_TRACK_JOB),

    canCreateUser: () => hasPermission(PERMISSIONS.CAN_CREATE_USER),
    canEditUser: () => hasPermission(PERMISSIONS.CAN_EDIT_USER),
    canDeleteUser: () => hasPermission(PERMISSIONS.CAN_DELETE_USER),

    canCrudDriver: () =>
      hasPermission(PERMISSIONS.CAN_CRUD_DRIVER_FOR_SELF_OR_TRANSPORTER),
    canCrudTransporter: () => hasPermission(PERMISSIONS.CAN_CRUD_TRANSPORTER),
    canCrudVehicle: () =>
      hasPermission(PERMISSIONS.CAN_CRUD_TRUCK_TRAILER_FOR_SELF_OR_TRANSPORTER),

    // User profile access
    userProfile,

    // Permission constants
    PERMISSIONS,
    PERMISSION_GROUPS,
  };
};
